/* eslint-disable complexity */
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { Display, FaClasses, setDisplay, toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_CENTERED, MARGIN, POSITION, SHADOW } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { filterNotDefined } from "@cervval.js/common/src/tools/collection.array.tools";
import { buildObjectRecord } from "@cervval.js/common/src/tools/collection.map.tools";
import { anyReallyAny, clone, sortFields } from "@cervval.js/common/src/tools/object.tools";
import { RowUid } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { Instance, createPopper } from "@popperjs/core";
import { DragAndDropEvent } from "../../services/dragAndDrop/dragAndDrop.event";
import { DRAG_AND_DROP_SERVICE, DragAndDropService } from "../../services/dragAndDrop/dragAndDrop.service";
import { CJS_DROP_CONTAINER_ATTR } from "../../services/dragAndDrop/dragAndDrop.types";
import { DragRef } from "../../services/dragAndDrop/dragRef";
import { CjsIconFa } from "../icon/iconFa";
import { IconFaSize, getIconFaName } from "../icon/iconFa.tools";
import { IconFA6 } from "../icon/tools/fontAwesome6IconList.tools";
import { CELL_CONTENT, CjsSmartTable, ROW_UID_ATTR, SmartTableRow } from "./smartTable";


/** Option for smartTable drag and drop */
export interface SmartTableDragAndDropOptions<ItemData> {

    /**
     * Enable drag and drop for smartTable
     * If set to false, other options has no effects
     * @default false
     */
    enable: boolean;

    /**
     * It's possible to drag multiple rows at the same time, 
     * When disableMultiRowDragging is set to false in the options and it is combined with selection.selectionType='multiple'.
     * If you disable multiRowDragging, this behavior will be disabled:
     * - When you select multiple items and drag one of them, all items in the selection will be dragged
     *
     * @default false
     */
    disableMultiRowDragging?: boolean;

    /** 
     * If true, a drag icon is added to row and row can be draggable
     * If false, no icon is added to row and row isn't draggable
     */
    canDragRow?: (row: SmartTableRow<ItemData>) => boolean;

    /** Options of drag text bubble */
    dragText?: {
        /** Disable rows drag text info bubble */
        disable?: boolean,
        /**
         * Get text for rows currently dragged
         */
        getRowsDragContent?: (rows: SmartTableRow<ItemData>[]) => AcceptedHtmlElementContent,

        /**
         * Disable "move" icon inside bubble, set it to true if you want a fully custom content
         */
        disableIcon?: boolean
    };

}

/** Uid of available drag and drop events */
export type SmartTableRowDragEventUid = "rowDragStart" | "rowDragMove" | "rowDragEnd";

export interface SmartTableRowDragEvent<ItemData> {

    /** One of rowDragEnter, rowDragMove, rowDragEnd, rowDragLeave */
    uid: SmartTableRowDragEventUid;

    /** The row getting dragged. Also the row that started the drag when multi-row dragging. */
    row: SmartTableRow<ItemData>;

    /** The list of rows being dragged. */
    rows: SmartTableRow<ItemData>[];

    /** The row index the mouse is dragging over or - 1 if over no row. */
    overIndex: number;

    /** The row node the mouse is dragging over or undefined if over no row. */
    overRow: SmartTableRow<ItemData> | undefined;

    /** 
     * The vertical pixel location the mouse is over, with 0 meaning the top of the first row.
     * This can be compared to the row.rowHeight and rowNode.rowTop to work out the mouse position relative to rows.
     * The provided attributes overIndex and overRow means the y property is mostly redundant.
     * The y property can be handy if you want more information such as 'how close is the mouse to the top or bottom of the row?' 
     */
    y: number;
}

/**
 * Context of drag and drop
 * Store all values needed to handle drag and drop inside smartTable
 */
export interface SmartTableDragContext<ItemData> {

    /** Uid of event (from smartTable) */
    eventUid: string;

    /** Id of smartTable who start drag */
    smartTableId: number;

    /** Row where the drag&drop start */
    startDraggingRow: SmartTableRow<ItemData> | null;

    /** Row where the drag&drop end */
    endDraggingRow: SmartTableRow<ItemData> | null;

    /** Store a clone of smartTable rows at start of drag&drop, used to compare before and after d&d rows order */
    initialRows: SmartTableRow<ItemData>[];

    /** 
     * Store a clone of smartTable rows, theses rows store the current d&d order.
     * This order will be applied to smartTable at end or d&d 
     */
    currentRows: SmartTableRow<ItemData>[];

    /**
     * Store rows to drag
     * It's possible to drag multiple rows at the same time, when rowDragMultiRow is set to true in the options
     * When you select multiple rows and drag one of them, all rows in the selection will be dragged.
     * When you drag a row that is not selected while other rows are selected, only the unselected rows will be dragged.
     * When you drag a row that is selected while other rows are selected but not draggable, only the draggable rows will be dragged.
     */
    rowsToDrag: SmartTableRow<ItemData>[];

    /** PopperJs instance of bubble info text, store it to destroy it at end of d&d */
    dragText: Instance | null;
}

/** Defined custom types for smartTable */
export type SmartTableDragRef<ItemData> = DragRef<SmartTableDragContext<ItemData>>;
export type SmartTableDragEvent<ItemData> = DragAndDropEvent<SmartTableDragContext<ItemData>>;
export type SmartTableDragEndEvent<ItemData> = DragAndDropEvent<SmartTableDragContext<ItemData>>;

/** Drag handler span CSS class */
export const DRAG_HANDLER = "drag-handler";

/**
 * SmartTable DragAndDrop helper (RowDragging)
 *
 * Row dragging is used to rearrange rows by dragging the row with the mouse.
 * This helper is available in all SmartTable of cervval.js framework
 * 
 * The logic for managed dragging is simple and has the following constraints:
 * Does not work if hierarchy is enabled.
 * Does not work when sorting is applied. This is because the sort order of the rows depends on the data and moving the data would break the sort order.
 * Does not work when filtering is applied. This is because a filter removes rows making it impossible to know what 'real' order of rows when some are missing.
 */
export class SmartTableDragAndDropHelper<ItemData> {

    /** Drag and drop service */
    @Injected(DRAG_AND_DROP_SERVICE)
    public readonly dragAndDropService!: DragAndDropService;

    /**
     * A drag has started, or dragging already started and the mouse has re-entered the table having previously left the table.
     */
    @Event()
    public rowDragStart!: EventHandler<SmartTableRowDragEvent<ItemData>>;

    /**
     * The mouse has moved while dragging
     */
    @Event()
    public rowDragMove!: EventHandler<SmartTableRowDragEvent<ItemData>>;

    /**
     * The drag has finished over the grid.
     */
    @Event()
    public rowDragEnd!: EventHandler<SmartTableRowDragEvent<ItemData>>;

    /**
     * A dragAndDropEnd event was triggered on smartTable, use it to add data to table
     */
    @Event()
    public dataDroppedIntoTableEvent!: EventHandler<DragAndDropEvent<anyReallyAny>>;

    /**
     * A dragAndDropEnd event was triggered on other smartTable, use it to remove data from table
     */
    @Event()
    public dataDroppedIntoOtherElementEvent!: EventHandler<DragAndDropEvent<anyReallyAny>>;

    /**
     * Interval id of callback for auto-scrolling table when user move a row on top or bottom of table
     * Store this interval id to reset interval when needed
     */
    private _tableScrollIntervalId: number | undefined;

    /**
     * Elements on top and bottom of displayed table
     * Each section will be displayed when user drag a row on top/bottom of tableBody
     * Add a visual feedback for user to understand where is the auto-scrolling zone
     */
    private _tableScrollZones = {
        /** Top auto-scrolling zone */
        top: createHtmlElement("div", { cssClass: ["table-scroll-shadow", "top", SHADOW.S2] }),
        /** Bottom auto-scrolling zone */
        bottom: createHtmlElement("div", { cssClass: ["table-scroll-shadow", "bottom", SHADOW.S2] })
    };

    /** 
     * Y margin for row re-order interaction trigger
     * in px
     * @see _getRowYBounds
     */
    public rowYInteractionMargin = 4;

    /** 
     * Auto scrolling top and bottom margin value
     * in px
     */
    public autoScrollingMargin = 20;

    /**
     * Auto scrolling debounce time
     * in ms
     */
    public autoScrollingDebounce = 400;

    constructor(private _smartTable: CjsSmartTable<ItemData>) {
        // Don't init anything in constructor, any dragAndDrop behavior should be declared only on user interactions
        this._initListeners();

        this.dragAndDropService.configureDropContainer(this._smartTable);
    }

    /** @returns true if dragAndDrop is enable for table */
    public enable(): boolean {
        return Boolean(this._smartTable.options.dragAndDrop?.enable) &&
            !this._smartTable.filterHelper.filters.some(f => f.value !== "") &&// No filter in table
            !this._smartTable.sortHelper.activeSortedColumns.length;// No sort in table
    }

    /** Apply new row order inside smartTable */
    public applyRowOrder(event: SmartTableDragEvent<ItemData>): void {

        const context = event.source.data;

        // Store new indexes to select
        const rowsToSelect: RowUid[] = [];

        const selectionModel = this._smartTable.getSelectionModel();

        // Apply new rows globalIndex to old rows
        const currentRowsByUid = buildObjectRecord(context.currentRows, "uid");
        for (const r of this._smartTable.tableRows) {
            const newGlobalIndex = currentRowsByUid[r.uid]?.globalIndex;
            if (newGlobalIndex == null) { continue; }

            const isRowSelected = selectionModel.isSelected(r.uid);
            if (isRowSelected) {
                // Store new index of selectedRow
                rowsToSelect.push(r.uid);
            }

            // Update row global index
            r.globalIndex = newGlobalIndex;
        }

        // Apply selection with new order
        selectionModel.select(rowsToSelect);

        // Sort rows by order inside "tableRows"
        sortFields(this._smartTable["_tableRows"], "globalIndex");

        // Refresh displayedRows cached array
        this._smartTable.updateDisplayableRows();

        // Refresh smartTable, always get a upToDate rendering
        this._smartTable.refresh();
    }

    /** Get table items ordered */
    public getTableItemsOrdered(): ItemData[] {
        return filterNotDefined(this._smartTable.tableRows.map(r => this._smartTable.getRowItemData(r)));
    }

    /** Init drag and drop helper listeners */
    private _initListeners(): void {
        this.dragAndDropService.dragStartEvent.on(this._dragStartSequence.bind(this));
        this.dragAndDropService.dragOverEvent.on(this._dragMoveSequence.bind(this));
        this.dragAndDropService.dragEndEvent.on(this._dragEndSequence.bind(this));
    }

    /** Handle start dragging sequence from service */
    private _dragStartSequence(event: CustomEvent<SmartTableDragEvent<ItemData>>): void {
        const dragEvent = event.detail;
        const context = dragEvent.source.data;

        // Check if table attached with helper is the table who start the drag sequence
        if (!this._isSelfEvent(dragEvent)) { return; }

        // Remove auto-scrolling interval if exist
        this._clearAutoScrollInterval();

        // Get the original row where user click
        const rowClicked = this._getRowFromEvent(dragEvent.event);

        if (rowClicked) {
            // Update drag context
            context.startDraggingRow = rowClicked;
            this._refreshContext(dragEvent);

            // Build drag text
            context.dragText = this._buildRowDragText(dragEvent);

            // Refresh drag&drop style
            this._refreshRowDragAndDropStyle(dragEvent);
            this._refreshTableScrollZones(dragEvent);


            // If drag has started, emit move event
            if (context.startDraggingRow) {

                // Emit drag start event
                this.rowDragStart.emit({
                    overRow: undefined,
                    overIndex: -1,
                    row: context.startDraggingRow,
                    rows: context.rowsToDrag,
                    uid: "rowDragStart",
                    y: dragEvent.event.pageY
                });
            }
        }
    }

    /** @returns true if smartTable is a drop container */
    public isDropContainer(): boolean {
        return this._smartTable.getAttribute(CJS_DROP_CONTAINER_ATTR) != null;
    }

    /** Mouse move handler */
    private _dragMoveSequence(event: CustomEvent<SmartTableDragEvent<ItemData>>): void {
        const dragEvent = event.detail;
        const context = dragEvent.source.data;
        const nativeEvent = dragEvent.event;
        // Check if table attached with helper is the table who start the drag sequence
        if (!this._isSelfEvent(dragEvent)) { return; }

        // Update drag text info bubble position from mouseEvent position
        if (context.dragText) {
            context.dragText.state.elements.reference.getBoundingClientRect = this._generateGetBoundingClientRect(nativeEvent.x, nativeEvent.y);
            context.dragText.update();
        }

        // If table isn't a drop container, don't re-order rows
        if (!this.isDropContainer()) { return; }

        // Check if mouse is inside table auto-scrolling zones
        const tableWasRefresh = this._checkTableScroll(dragEvent);
        // Don't re-order rows if table was refresh, made inside auto-scroll
        if (tableWasRefresh) { return; }

        // Get the row over
        const rowOver = this._getRowFromEvent(dragEvent.event);

        // If a row is over, refresh rows order
        if (rowOver) {
            const rowOrderHasChange = this._refreshRowsOrder(rowOver, dragEvent);

            // If row order has change, refresh rows position inside table
            if (rowOrderHasChange) {
                this._refreshRowsPosition(dragEvent);
            }
        }

        // If drag has started, emit move event
        if (context.startDraggingRow) {

            // Emit drag start event
            this.rowDragMove.emit({
                overRow: rowOver,
                overIndex: rowOver?.globalIndex ?? -1,
                row: context.startDraggingRow,
                rows: context.rowsToDrag,
                uid: "rowDragMove",
                y: nativeEvent.pageY
            });
        }
    }

    /** Mouse up handler, end of drag and drop */
    private _dragEndSequence(event: CustomEvent<SmartTableDragEndEvent<ItemData>>): void {
        const dragEvent = event.detail;
        const context = dragEvent.source.data;
        // Get the end row
        const rowMouseUp = this._getRowFromEvent(dragEvent.event);
        dragEvent.data.endDraggingRow = rowMouseUp ?? null;
        // Check if table attached with helper is the table who start the drag sequence
        if (!this._isSelfEvent(dragEvent)) {
            this._dragEndSequenceFromOtherElement(dragEvent);
            return;
        }

        // Remove auto-scrolling interval if exist
        this._clearAutoScrollInterval();

        // Remove drag text
        context.dragText?.state.elements.popper.remove();
        context.dragText?.destroy();

        // Apply new order inside smartTable
        this.applyRowOrder(dragEvent);

        // Hide auto-scrolling zones
        this._refreshTableScrollZones(dragEvent);

        // Emit drag end event
        if (context.startDraggingRow) {
            this.rowDragEnd.emit({
                overRow: rowMouseUp,
                overIndex: rowMouseUp?.globalIndex ?? -1,
                row: context.startDraggingRow,
                rows: context.rowsToDrag,
                uid: "rowDragEnd",
                y: dragEvent.event.pageY
            });
        }

        // If data was dropped inside a drop container who isn't this table body, dispatch event
        // Use this event to remove data from table in most cases
        if (dragEvent.dropContainer && dragEvent.dropContainer !== this._smartTable) {
            this.dataDroppedIntoOtherElementEvent.emit(dragEvent);
        }

        // Reset context
        dragEvent.source.data = this._getEmptyContext();
    }

    /** Mouse up handler */
    private _dragEndSequenceFromOtherElement(event: SmartTableDragEndEvent<anyReallyAny>): void {

        // Check if table attached with helper isn't the table who start the drag sequence
        if (this._isSelfEvent(event)) { return; }

        // Check if item was dropped inside this smartTable
        if (event.dropContainer !== this._smartTable) { return; }

        // Emit data was dropped inside table
        this.dataDroppedIntoTableEvent.emit(event);
    }


    /** Remove auto-scrolling interval if exist */
    private _clearAutoScrollInterval(): void {
        if (this._tableScrollIntervalId) {
            clearInterval(this._tableScrollIntervalId);
            this._tableScrollIntervalId = undefined;
        }
    }

    /** Get row clicked from event */
    private _getRowFromEvent(e: Event): SmartTableRow<ItemData> | undefined {
        // Get the original row of target
        const element = e.target as HTMLElement;
        const rowClicked = element.closest("tr");// Closest row contains row uid
        if (!rowClicked) { return undefined; }// Should never append

        // Retrieve row clicked
        const rowUid = rowClicked.getAttribute(ROW_UID_ATTR) ?? "";
        return this._smartTable.tableRows.find(r => r.uid === rowUid);// Should always return a row
    }

    /**
     * Refresh row ordering in current context
     * Compute the new rows order from row currently over by user
     * * ! RowOver is the row inside SmartTable tableRows list, not row in current d&d context with upToDate index
     */
    private _refreshRowsOrder(rowOver: SmartTableRow<ItemData>, event: SmartTableDragEvent<ItemData>): boolean {

        const context = event.source.data;
        const dragRef = event.source;
        const moveEvent = event.event;

        // Check drag&drop has started
        const startRow = context.startDraggingRow;
        if (!dragRef.isDraggingStarted || !startRow) { return false; }

        // If row currently over is the start row, nothing to do, no ordering to compute
        if (startRow.uid === rowOver.uid) { return false; }

        // If row currently over is a row to drag (startRow or selected row), nothing to do, ordering should not change
        if (this._isRowToDrag(rowOver, event)) { return false; }

        // Define drag&drop vertical direction, based on row Y and moveEvent Y difference
        const vDirection = event.vDirection;
        const isUp = vDirection === "up";

        // Define top and bottom bound in Y axis for rowOver,
        // Used to trigger row ordering only if user move over a defined margin(default: 4px), not immediately on first over
        const rowOverYBounds = this._getRowYBounds(rowOver);

        // Check if user over row inside YBounds margin
        if (
            rowOverYBounds &&
            (
                (isUp && rowOverYBounds.bottom < moveEvent.y) ||
                (!isUp && rowOverYBounds.top > moveEvent.y)
            )
        ) { return false; }// If rowOver bounds don't match with mouseY, don't apply rows reordering

        // Get lower and upper indexes of including rowOver
        const startEndIndexes = this._getStartEndRowsOrderIndexes(rowOver, isUp, event);
        if (!startEndIndexes) { return false; }// Should never append
        const { startIndex, endIndex } = startEndIndexes;

        // Retrieve rows between start row and over row, can be multiple rows
        // Exclude rowsToDrag from list, their new order will be computed later
        const rowsBetween = context.currentRows.slice(startIndex, endIndex).filter(r => !this._isRowToDrag(r, event));
        if (!rowsBetween.length) { return false; }

        // Get all rows to drag
        const rowsToDrag = context.rowsToDrag;

        // Direction up
        if (vDirection === "up") {// TODO Fix multiple selection not sibling

            // Get last row to drag index, this index will be the index of last rowOver index (in single mode, it's startRow.index)
            let previousRowIndex = rowsToDrag[rowsToDrag.length - 1].globalIndex;

            // Update rows between order, reverse and decrement to respect rows siblings order
            for (const rowBetween of rowsBetween.reverse()) {
                rowBetween.globalIndex = previousRowIndex;
                previousRowIndex--;
            }

            const rowsToDragWithRefByUid = buildObjectRecord(context.rowsToDrag, "uid");
            // Update rows to drag order
            for (const rowToDrag of clone(rowsToDrag).reverse()) {// Should clone rowsToDrag because reverse overwrites the original array
                rowsToDragWithRefByUid[rowToDrag.uid].globalIndex = previousRowIndex;
                previousRowIndex--;
            }


        } else {// TODO Fix multiple selection not sibling

            // Get first row to drag, this index will be the next rowOver index (in single mode, it's startRow.index)
            let nextRowIndex = rowsToDrag[0].globalIndex;

            // Update rows between order
            for (const rowBetween of rowsBetween) {
                rowBetween.globalIndex = nextRowIndex;
                nextRowIndex++;
            }

            // Update rows to drag order
            for (const rowToDrag of rowsToDrag) {
                rowToDrag.globalIndex = nextRowIndex;
                nextRowIndex++;
            }

        }

        // Sort rows by order in context
        sortFields(context.currentRows, "globalIndex");

        // Ensure all rows got an unique globalIndex
        context.currentRows.forEach((r, i) => r.globalIndex = i);


        // Ordering was performed, return true
        return true;
    }

    /**
     * Get start and end indexes for refreshOrder method
     * To handle multiple selection, we need to retrieve the maximum indexes range.
     * Will return indexes ranges for current drag:
     * In up:
     *  - startIndex: lower index between firstRowToDrag and rowOver
     *  - endIndex: lastRowToDrag
     * 
     * In down:
     *  - startIndex: firstRowToDrag
     *  - endIndex: upper index between lastRowToDrag and rowOver
     */
    private _getStartEndRowsOrderIndexes(rowOver: SmartTableRow<ItemData>, isUp: boolean, event: SmartTableDragEvent<ItemData>): { startIndex: number, endIndex: number } | undefined {
        const context = event.source.data;

        // Get all rows to drag
        const rowsToDrag = context.rowsToDrag;

        // Retrieve start row and row over index inside context
        const firstRowToDrag = rowsToDrag[0];
        const lastRowToDrag = rowsToDrag[rowsToDrag.length - 1];

        let firstRowToDragIndex;
        let lastRowToDragIndex;
        let rowOverIndex;

        // One loop to find all indexes, better performances
        for (let i = 0; i < context.currentRows.length; i++) {
            const row = context.currentRows[i];
            if (row.uid === firstRowToDrag.uid) { firstRowToDragIndex = i; }
            if (row.uid === lastRowToDrag.uid) { lastRowToDragIndex = i; }
            if (row.uid === rowOver.uid) { rowOverIndex = i; }

            // All indexes found, useless to continue
            if (firstRowToDragIndex != null && lastRowToDragIndex != null && rowOverIndex != null) { break; }
        }

        // Should never append
        if (firstRowToDragIndex == null || lastRowToDragIndex == null || rowOverIndex == null) { return undefined; }

        // In up, take lower index between firstRowToDrag and rowOver
        let startIndex = isUp ? Math.min(firstRowToDragIndex, rowOverIndex) : firstRowToDragIndex;
        // In down, take upper index between lastRowToDrag and rowOver
        let endIndex = !isUp ? Math.max(lastRowToDragIndex, rowOverIndex) : lastRowToDragIndex;

        if (!isUp) {// Down
            // Increments indexes to retrieve rows strict after startRow and include overRow (.slice in js include start and exclude finish index)
            startIndex++;
            endIndex++;
        }

        return { startIndex, endIndex };
    }


    /**
     * Refresh row position inside table, will move the rows with transform:translateY(value) if needed
     */
    private _refreshRowsPosition(event: SmartTableDragEvent<ItemData>): void {
        const context = event.source.data;
        const dragRef = event.source;

        // Check drag&drop has started
        const startRow = context.startDraggingRow;
        if (!dragRef.isDraggingStarted || !startRow) { return; }

        // Build rows record by uid for performance
        const originalRowsByUid = buildObjectRecord(context.initialRows, "uid");
        const smartTableRowsByUid = buildObjectRecord(this._smartTable.tableRows, "uid");

        // Compute startRowHeight only once
        // ! Can't use smartTable.getRowHeight, value returned don't include borders height
        const startRowHeight = this._getRowTr(startRow)?.clientHeight ?? 0;

        // Loop into all drag&drop current context
        for (const currentRow of context.currentRows) {

            // Retrieve the original row index
            const originalIndex = originalRowsByUid[currentRow.uid].globalIndex;

            // Retrieve the <tr> element of row currently displayed inside smartTable
            const smartTableRow = smartTableRowsByUid[currentRow.uid];
            const smartTableRowTr = smartTableRow?.tr;

            // Check if row order has changed between current ordering and initial ordering
            const indexDiff = currentRow.globalIndex - originalIndex;
            if (indexDiff !== 0) {// If different from "0", order has changed

                if (currentRow.uid !== startRow.uid) {// FIXME invalid translate height for rowsToDrag
                    if (smartTableRowTr) {// Should always append, if row is rendered
                        smartTableRowTr.classList.add("drag-animation");
                        smartTableRowTr.style.transform = "translateY(" + (startRowHeight * indexDiff) + "px)";
                    }

                } else {// Should only append once (startRow)

                    // Retrieve start row and row over index inside context
                    let startIndex = context.initialRows.findIndex(r => r.uid === currentRow.uid);
                    let currentIndex = context.currentRows.findIndex(r => r.uid === currentRow.uid);

                    // Check if order is up with indexes compare
                    const isUp = startIndex > currentIndex;

                    // If up re-ordering, switch indexes ascending order
                    if (isUp) {
                        [startIndex, currentIndex] = [currentIndex, startIndex];
                    }

                    // Get all rows between startIndex and currentIndex
                    const rowsBetween = context.currentRows.slice(startIndex, currentIndex);


                    // Compute all rows between total height
                    let heightOffset = 0;
                    rowsBetween.forEach(r => {
                        heightOffset += smartTableRowsByUid[r.uid]?.tr?.clientHeight ?? 0;
                    });

                    // Apply transform to startRow
                    if (smartTableRowTr) {
                        smartTableRowTr.classList.add("drag-animation");

                        const heightDiffPx = heightOffset * (isUp ? -1 : 1);
                        smartTableRowTr.style.transform = `translateY(${heightDiffPx}px)`;
                    }
                }
            } else if (smartTableRowTr) {// No order update, remove transform style
                smartTableRowTr.style.transform = "";
            }
        }
    }

    /** Refresh style of drag and drop from current context */
    private _refreshRowDragAndDropStyle(event: SmartTableDragEvent<ItemData>): void {
        const smartTableRowsByUid = buildObjectRecord(this._smartTable.tableRows, "uid");

        const context = event.source.data;
        // Add or remove "dragged" class of all rowsToDrag
        for (const rowToDrag of context.rowsToDrag) {
            const tr = smartTableRowsByUid[rowToDrag.uid].tr;
            if (!tr) { continue; }
            toggleClass(tr, event.source.isDraggingStarted, "dragged");
        }
    }

    /** Refresh table scroll top and bottom shadows */
    private _refreshTableScrollZones(event: SmartTableDragEvent<ItemData>): void {
        const dragRef = event.source;

        // if drag start, append zones to smartTable (default: display=none)
        if (dragRef.isDraggingStarted) {
            // Top zone to tableBody
            this._smartTable.components.tableBody.append(this._tableScrollZones.top);
            // Bottom zone to smartTableContainer (we want the zone to appear on bottom of displayed rows)
            this._smartTable.components.smartTableContainer.append(this._tableScrollZones.bottom);
        } else {// If drag end, remove zones
            this._tableScrollZones.top.remove();
            this._tableScrollZones.bottom.remove();
        }

        // Add position relative to containers during drag
        toggleClass(this._smartTable.components.tableBody, dragRef.isDraggingStarted, POSITION.RELATIVE);
        toggleClass(this._smartTable.components.smartTableContainer, dragRef.isDraggingStarted, POSITION.RELATIVE);

    }

    /** 
     * Get Y bounds for given row, used to define a colliding box to trigger row re-ordering
     * Margin: default = 4px
     * 
     * #Row
     * ___________
     * | Margin top 
     * - - - - - -  <|Top value
     *              <|
     * #Content     <|
     *              <|
     * - - - - - -  <|Bottom value
     * | Margin bottom (default: 4px)
     * ___________
     * 
     * 
     */
    private _getRowYBounds(row: SmartTableRow<ItemData>): { top: number, bottom: number } | undefined {
        const bounds = row.tr?.getBoundingClientRect();
        if (!bounds?.width) { return undefined; }// Check bounds and width, equal 0 if row isn't append to DOM (append during auto-scrolling)

        const margin = this.rowYInteractionMargin;
        return {
            top: bounds.y + margin,
            bottom: bounds.y + bounds.height - margin
        };
    }

    /**
     * Get rows to drag
     * It is possible to drag multiple rows at the same time, when rowDragMultiRow is set to true in the options
     * When you select multiple rows and drag one of them, all rows in the selection will be dragged.
     * When you drag a row that is not selected while other rows are selected, only the unselected rows will be dragged.
     * When you drag a row that is selected while other rows are selected but not draggable, only the draggable rows will be dragged.
     */
    private _getRowsToDrag(currentRows: SmartTableRow<ItemData>[], event: SmartTableDragEvent<ItemData>): SmartTableRow<ItemData>[] {
        const startDraggingRow = event.source.data.startDraggingRow;
        if (!startDraggingRow) { return []; }

        // Get selected rows in table
        const selectedRows = this._smartTable.getSelectedRows();
        const currentRowsByUid = buildObjectRecord(currentRows, "uid");

        // Check multiDragDragging isn't disable and startDragging row is a selected row
        if (!this._smartTable.options.dragAndDrop?.disableMultiRowDragging && selectedRows.some(r => r.uid === startDraggingRow.uid)) {
            return selectedRows.filter(r => this._canDragRow(r)).map(r => currentRowsByUid[r.uid]);
        } else {// Return only startDragging row
            return [currentRowsByUid[startDraggingRow.uid]];
        }
    }

    /** @returns true if row is a row to drag */
    private _isRowToDrag(rowToCheck: SmartTableRow<ItemData>, event: SmartTableDragEvent<ItemData>): boolean {
        return event.source.data.rowsToDrag.some(r => r.uid === rowToCheck.uid);
    }

    /** 
     * Check table scrolling, handle rows not generated
     * If user move a row to top or bottom of table, add an auto scroll to table
     */
    private _checkTableScroll(event: SmartTableDragEvent<ItemData>): boolean {
        const nativeEvent = event.event;
        const context = event.source.data;

        // Clear previous auto-scrolling interval
        clearInterval(this._tableScrollIntervalId);

        // Get table bounds (already computed from last render)
        const tableBounds = this._smartTable.getTableBounds();

        // Get auto-scrolling margin detection value
        const margin = this.autoScrollingMargin;

        if (nativeEvent.y - tableBounds.body.top < margin) {

            // returns true if first row of table is currently displayed
            const isFirstRowDisplayed = (): boolean => this._smartTable.options.firstDisplayedItemIndex === 0;

            // UP auto-scroll
            const up = (): void => {
                if (isFirstRowDisplayed()) { return; }// Nothing to scroll, already at start of table scroll

                // Decrement scroll index, refresh smartTable
                const previousIndex = this._smartTable.options.firstDisplayedItemIndex - 1;
                this._smartTable.options.setWithoutRefresh({ firstDisplayedItemIndex: previousIndex });


                // Refresh rows order with new row displayed, place rowsToDrag upper new row rendered
                const firstRowToDrag = context.rowsToDrag[0];
                const startRowIndex = context.currentRows.findIndex(r => r.uid === firstRowToDrag?.uid);
                const previousRow = context.currentRows[startRowIndex - 1];
                if (previousRow) {
                    // Refresh row ordering
                    this._refreshRowsOrder(previousRow, event);
                }

                this.applyRowOrder(event);
                this._refreshContext(event);
                // Refresh d&d style to rows and zones
                this._refreshRowDragAndDropStyle(event);
                this._refreshTableScrollZones(event);

            };

            const firstRowIsDisplayed = isFirstRowDisplayed();

            // Display top zone only if first zone isn't displayed
            if (!firstRowIsDisplayed) { setDisplay(this._tableScrollZones.top, Display.FLEX); }

            // Start up auto-scroll interval
            this._tableScrollIntervalId = setInterval(up, this.autoScrollingDebounce);
            return !firstRowIsDisplayed;
        }
        // Hide top zone if no up check
        setDisplay(this._tableScrollZones.top, Display.NONE);


        // Check bottom scrolling bounds
        if (tableBounds.body.bottom - nativeEvent.y < margin) {

            // returns true if last row of table is currently displayed
            const isLastRowDisplayed = (): boolean => this._smartTable.displayedRows.at(-1)?.globalIndex === this._smartTable.tableRows.at(-1)?.globalIndex;

            // DOWN auto-scroll
            const down = (): void => {
                if (isLastRowDisplayed()) { return; }// Nothing to scroll, already at end of table scroll

                // Increment scroll index, refresh smartTable
                const nextIndex = this._smartTable.options.firstDisplayedItemIndex + 1;
                this._smartTable.options.setWithoutRefresh({ firstDisplayedItemIndex: nextIndex });


                // Refresh rows order with new row displayed, place rowsToDrag under new row rendered
                const lastRowToDrag = context.rowsToDrag.at(-1);
                const startRowIndex = context.currentRows.findIndex(r => r.uid === lastRowToDrag?.uid);
                const nextRow = context.currentRows[startRowIndex + 1];
                if (nextRow) {
                    // Refresh row ordering
                    this._refreshRowsOrder(nextRow, event);
                }

                this.applyRowOrder(event);
                this._refreshContext(event);

                // Refresh d&d style to rows and zones
                this._refreshRowDragAndDropStyle(event);
                this._refreshTableScrollZones(event);
            };


            const lastRowIsDisplayed = isLastRowDisplayed();
            if (!lastRowIsDisplayed) {
                setDisplay(this._tableScrollZones.bottom, Display.FLEX);
            }

            this._tableScrollIntervalId = setInterval(down, this.autoScrollingDebounce);
            return !lastRowIsDisplayed;
        }
        // Hide bottom zone if no down check
        setDisplay(this._tableScrollZones.bottom, Display.NONE);

        return false;
    }

    /** Build rows drag info bubble */
    private _buildRowDragText(event: SmartTableDragEvent<ItemData>): Instance {
        const context = event.source.data;
        const nativeEvent = event.event;

        // Is multiple rows to drag ?
        const multiple = context.rowsToDrag.length > 1;

        let content = this._smartTable.options.dragAndDrop?.dragText?.getRowsDragContent?.(context.rowsToDrag);
        if (!content) {
            // Build default content
            content = createHtmlElement("span", {
                innerHTML: `${context.rowsToDrag.length} élement${multiple ? "s" : ""} sélectionné${multiple ? "s" : ""}`// TODO i18n
            });
        }

        const infoBubble = createHtmlElement("div", {
            cssClass: [FLEX_CONTAINER_CENTERED, POSITION.ABSOLUTE, "row-drag-popup"],
            innerHTML: [content],
            style: {
                zIndex: "2"
            }
        });

        // Add icon to infoBubble if not disabled (default: not disabled)
        if (!this._smartTable.options.dragAndDrop?.dragText?.disableIcon) {
            // Drag move icon
            const icon = new CjsIconFa({
                name: getIconFaName(IconFA6.ARROWS_UP_DOWN_LEFT_RIGHT),
                size: IconFaSize.LG
            });

            icon.classList.add(MARGIN.ME_2);
            infoBubble.prepend(icon);
        }

        // Define a virtual element, linked to cursor
        const virtualElement = {
            getBoundingClientRect: this._generateGetBoundingClientRect(nativeEvent.pageX, nativeEvent.pageY)
        };
        // Add infoBubble to smartTable
        this._smartTable.append(infoBubble);

        // Modifier for offset attribute
        const offsetModifier = {
            name: "offset",
            options: {
                offset: [18, 18]
            }
        };

        // Create popper instance
        return createPopper(virtualElement, infoBubble, {
            placement: "bottom-start",
            modifiers: [offsetModifier]
        });
    }

    /** @returns true if row can be dragged */
    private _canDragRow(row: SmartTableRow<ItemData>): boolean {
        return this._smartTable.options.dragAndDrop?.canDragRow?.(row) ?? true;
    }

    /** Get drag handler inside a row */
    public refreshCellDragHandler(row: SmartTableRow<ItemData>): void {
        row.dragHandler?.element.remove();

        if (!this.enable()) {// Remove dragHandler if dragAndDrop is disabled
            row.dragHandler = undefined;
            return;
        }

        if (!row.tr) { return; }

        // Add drag handler is dragAndDrop is enable

        const cellContentContainer = row.tr.querySelector(`.${CELL_CONTENT}`) as HTMLDivElement | undefined;
        if (!cellContentContainer) { return; }// Should not append

        const dragHandler = createHtmlElement("span", {
            cssClass: [DRAG_HANDLER]
        });
        const dragRef = this.dragAndDropService.createDrag<SmartTableDragContext<ItemData>>(
            dragHandler,
            this._getEmptyContext(),
            {
                disabled: () => !this._canDragRow(row)
            }
        );

        if (this._canDragRow(row)) {
            // Add icon
            dragHandler.append(createHtmlElement("i", {
                cssClass: [FaClasses.SOLID, getIconFaName(IconFA6.GRID_HORIZONTAL)]
            }));
        }

        // Assign dragHandler to row
        row.dragHandler = dragRef;

        // Add drag handler icon before first column cell content
        cellContentContainer.prepend(dragHandler);
    }

    /** Get upToDate row tr */
    private _getRowTr(row: SmartTableRow<ItemData>): HTMLTableRowElement | undefined {
        return this._smartTable.tableRows.find(r => r.uid === row.uid)?.tr;
    }

    /** Refresh drag context rows */
    private _refreshContext(event: SmartTableDragEvent<ItemData>): void {
        const context = event.source.data;
        context.initialRows = clone(this._smartTable.tableRows);
        context.currentRows = clone(this._smartTable.tableRows);
        context.rowsToDrag = this._getRowsToDrag(context.currentRows, event);
    }

    /** 
     * Tool for Popper, generate a fake boundingClientRect for virtual element placement, 
     * will follow mouse position
     */
    private _generateGetBoundingClientRect(x = 0, y = 0): () => DOMRect {
        return () => ({
            width: 0,
            height: 0,
            top: y,
            right: x,
            bottom: y,
            left: x,
            x,
            y,
            toJSON: () => ""
        });
    }

    /** Get a new empty drag&drop context */
    private _getEmptyContext(): SmartTableDragContext<ItemData> {
        return {
            startDraggingRow: null,
            endDraggingRow: null,
            dragText: null,
            initialRows: [],
            currentRows: [],
            rowsToDrag: [],
            eventUid: "smartTable",
            smartTableId: this._smartTable.componentNumber
        };
    }

    /** @returns true if given event is from helper linked smartTable */
    private _isSelfEvent(event: SmartTableDragEvent<ItemData>): boolean {
        const context = event.data;
        return context.smartTableId === this._smartTable.componentNumber;
    }
}